import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { makeStyles } from '@mui/styles';
import { useCourseContent } from '@openclassrooms/data-connect-web/learning-content';
import {
  useCourseComplete,
  useCourseIncomplete,
} from '@openclassrooms/data-connect-web/learning-activity';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Section from '@classify/react-core-components/Section';
import { RichContent } from '@openclassrooms/ui-web/rich-content';
import { scrollToElement } from '@openclassrooms/ui-web/scroll';
import Button from '@classify/react-core-components/Button';
import { translate } from '@openclassrooms/translations';
import { CourseStatus } from '@openclassrooms/sdk-web';

import CourseContext from '../../contexts/courseContext';
import SinglePageCourseContentSkeleton from './SinglePageCourseContentSkeleton';

const useStyles = makeStyles(({ spacing }) => ({
  contentWrapper: {
    marginTop: spacing(1),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const SinglePageCourseContent = () => {
  const classes = useStyles();
  const { currentUser, courseId, course, refetchCourseData } = useContext(CourseContext);
  const [completeCourse] = useCourseComplete();
  const [incompleteCourse] = useCourseIncomplete();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const courseStatus = searchParams.get('status') as CourseStatus | undefined;

  const { data: courseContent, isLoading } = useCourseContent({
    courseId,
    queryParams: {
      status: courseStatus,
    },
  });

  const handleCourseCompletion = async (isCompleted = true) => {
    if (isCompleted) {
      await completeCourse(
        { userId: currentUser.id, courseId },
        {
          onSuccess: () => {
            refetchCourseData();
            scrollToElement(document.getElementById('course-hero'), {
              offset: 0,
            });
          },
        }
      );
    } else {
      await incompleteCourse(
        { userId: currentUser.id, courseId },
        {
          onSuccess: () => {
            refetchCourseData();
            scrollToElement(document.getElementById('course-hero'), {
              offset: 0,
            });
          },
        }
      );
    }
  };

  return (
    <Section paddingVariant="reduced">
      <ContentWrapper variant="narrow" className={classes.contentWrapper}>
        <div aria-busy={isLoading}>
          {isLoading ? (
            <SinglePageCourseContentSkeleton />
          ) : (
            <div className={classes.content}>
              <RichContent content={courseContent} shouldZoomIntoImage />

              {course.userCourseCompletionRate !== 100 && currentUser && (
                <Button sx={{ mt: 1 }} onClick={() => handleCourseCompletion(true)}>
                  {translate('course.mark_complete.button')}
                </Button>
              )}

              {course.userCourseCompletionRate === 100 && currentUser && (
                <Button sx={{ mt: 1 }} onClick={() => handleCourseCompletion(false)}>
                  {translate('course.mark_incomplete.button')}
                </Button>
              )}
            </div>
          )}
        </div>
      </ContentWrapper>
    </Section>
  );
};

export default SinglePageCourseContent;
